import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FlaskConical, 
  ListTodo, 
  Bot,
  Menu,
  X,
  Zap,
  Target,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
  Activity,
  Brain,
  Sparkles,
  Users,
  DollarSign,
  Code,
  Database,
  Settings,
  Shield,
  ChevronDown,
  User,
  LogOut,
  CreditCard,
  Link as LinkIcon,
  MessageCircle,
  UserPlus
} from 'lucide-react';
import AIChat from './AIChat';
import { useAuth } from '../contexts/AuthContext';
import SubscriptionBanner from './SubscriptionBanner';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<string>('');
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, signOut } = useAuth();

  // Core navigation items (always visible)
  const navigationItems = [
    { name: 'Strategic Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'Experiment Hub', path: '/experiments', icon: FlaskConical },
    { name: 'Prioritised Backlog', path: '/backlog', icon: ListTodo },
    { name: 'Market Insights', path: '/market-insights', icon: TrendingUp },
    { name: 'Integrations', path: '/integrations', icon: LinkIcon },
  ];

  // Role-specific dashboard items for dropdown
  const roleSpecificItems = [
    // CPO Dashboards
    { name: 'Product Strategy', path: '/product-dashboard', icon: Target, roles: ['cpo', 'product_manager'] },
    { name: 'Customer Dashboard', path: '/customer-dashboard', icon: Users, roles: ['cpo', 'cmo', 'product_manager'] },
    
    // CTO Dashboards
    { name: 'Technology Excellence', path: '/technology-dashboard', icon: Zap, roles: ['cto', 'head_of_engineering'] },
    { name: 'Engineering & Delivery', path: '/engineering-dashboard', icon: Code, roles: ['cto', 'head_of_engineering'] },
    { name: 'System Health', path: '/system-health', icon: Activity, roles: ['cto', 'head_of_engineering'] },
    { name: 'Developer Experience', path: '/developer-experience', icon: Code, roles: ['cto', 'head_of_engineering'] },
    
    // CFO Dashboards
    { name: 'Financial Performance', path: '/financial-dashboard', icon: DollarSign, roles: ['cfo'] },
    { name: 'ROI Analysis', path: '/roi-analysis', icon: BarChart3, roles: ['cfo', 'cpo'] },
    { name: 'Cost Optimization', path: '/cost-optimization', icon: Target, roles: ['cfo'] },
    
    // CMO Dashboards
    { name: 'Marketing Performance', path: '/marketing-dashboard', icon: TrendingUp, roles: ['cmo'] },
    { name: 'Brand Health', path: '/brand-health', icon: Users, roles: ['cmo'] },
    
    // Data & Analytics Dashboards
    { name: 'Data & Analytics', path: '/data-dashboard', icon: Database, roles: ['head_of_data', 'data_scientist'] },
    { name: 'Model Performance', path: '/model-performance', icon: Brain, roles: ['data_scientist', 'head_of_data'] },
    { name: 'Insight Tracking', path: '/insight-tracking', icon: Sparkles, roles: ['head_of_data', 'cpo'] },
  ];

  // For demo purposes, show all dashboards (in real app, filter by role)
  const accessibleRoleItems = roleSpecificItems;

  const getRoleIcon = (roleId: string) => {
    switch (roleId) {
      case 'cpo': return <Target className="w-4 h-4" />;
      case 'cto': return <Zap className="w-4 h-4" />;
      case 'cfo': return <DollarSign className="w-4 h-4" />;
      case 'cmo': return <TrendingUp className="w-4 h-4" />;
      case 'head_of_data': return <Database className="w-4 h-4" />;
      case 'head_of_engineering': return <Code className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getRoleColor = (roleId: string) => {
    switch (roleId) {
      case 'cpo': return 'text-blue-400';
      case 'cto': return 'text-purple-400';
      case 'cfo': return 'text-green-400';
      case 'cmo': return 'text-orange-400';
      case 'head_of_data': return 'text-cyan-400';
      case 'head_of_engineering': return 'text-pink-400';
      default: return 'text-gray-400';
    }
  };

  const getRoleName = (roleId: string) => {
    switch (roleId) {
      case 'cpo': return 'Chief Product Officer';
      case 'cto': return 'Chief Technology Officer';
      case 'cfo': return 'Chief Financial Officer';
      case 'cmo': return 'Chief Marketing Officer';
      case 'head_of_data': return 'Head of Data & Analytics';
      case 'head_of_engineering': return 'Head of Engineering';
      default: return 'Team Member';
    }
  };

  // Enhanced dynamic prompts based on current page
  const getDynamicPrompts = () => {
    const currentPath = location.pathname;
    
    switch (currentPath) {
      case '/':
        return [
          {
            id: 'analyse-kpis',
            icon: BarChart3,
            text: 'Analyse current KPI trends',
            prompt: 'Based on the current performance data showing 96% vs 90% target, analyse the key factors driving this success and identify potential risks to maintaining this trajectory. Focus on the AI personalisation engine impact and mobile optimisation results.',
            color: 'text-blue-400 hover:text-blue-300',
            category: 'Performance Analysis'
          },
          {
            id: 'generate-hypotheses',
            icon: Lightbulb,
            text: 'Generate experiment hypotheses',
            prompt: 'Given the strong AI personalisation signals (+23% engagement) and mobile commerce growth (+45%), suggest 3 high-impact experiment hypotheses for Q2. Include expected outcomes, success metrics, and resource requirements.',
            color: 'text-yellow-400 hover:text-yellow-300',
            category: 'Strategic Planning'
          },
          {
            id: 'risk-mitigation',
            icon: AlertTriangle,
            text: 'Suggest risk mitigation strategies',
            prompt: 'Review the current risk distribution (35% market risk, 30% competitive risk) and recommend specific mitigation strategies for the highest priority threats. Include timeline and resource allocation.',
            color: 'text-red-400 hover:text-red-300',
            category: 'Risk Management'
          }
        ];
      
      case '/experiments':
        return [
          {
            id: 'prioritise-experiments',
            icon: Target,
            text: 'Re-prioritise experiment queue',
            prompt: 'Use the appropriate prioritisation technique to re-order the experiments in line with greatest impact and the key KPIs they could validate. Consider current confidence levels and resource constraints.',
            color: 'text-indigo-400 hover:text-indigo-300',
            category: 'Experiment Management'
          },
          {
            id: 'hypothesis-validation',
            icon: FlaskConical,
            text: 'Validate hypothesis strength',
            prompt: 'Critically evaluate the experiments run and summarise the strength of hypotheses with reasoning. Provide a recommendation for Kill/Pivot/Commit/Scale for each experiment based on current performance data.',
            color: 'text-purple-400 hover:text-purple-300',
            category: 'Hypothesis Testing'
          },
          {
            id: 'experiment-insights',
            icon: Sparkles,
            text: 'Extract experiment insights',
            prompt: 'Explain insights derived from experiments and how they map to strategic goals and KPIs. Identify patterns and correlations that could inform future hypothesis generation.',
            color: 'text-green-400 hover:text-green-300',
            category: 'Data Analysis'
          }
        ];
      
      case '/backlog':
        return [
          {
            id: 'update-priorities',
            icon: Target,
            text: 'Update backlog priorities',
            prompt: 'Based on current business value scores and risk assessments, recommend priority adjustments for the backlog items, especially considering the critical AI Personalisation Engine. Include impact on delivery timeline.',
            color: 'text-orange-400 hover:text-orange-300',
            category: 'Priority Management'
          },
          {
            id: 'resource-optimisation',
            icon: Zap,
            text: 'Optimise resource allocation',
            prompt: 'Analyse the current workload distribution (Sarah Chen: 21h, Mike Rodriguez: 13h) and suggest optimal resource allocation for maximum business impact. Consider skill sets and project dependencies.',
            color: 'text-cyan-400 hover:text-cyan-300',
            category: 'Resource Planning'
          },
          {
            id: 'dependency-analysis',
            icon: Activity,
            text: 'Analyse task dependencies',
            prompt: 'Review the backlog for potential dependencies and bottlenecks, particularly around security audit requirements and mobile optimisation tasks. Suggest mitigation strategies for critical path items.',
            color: 'text-pink-400 hover:text-pink-300',
            category: 'Dependency Management'
          }
        ];
      
      case '/market-insights':
        return [
          {
            id: 'market-analysis',
            icon: TrendingUp,
            text: 'Analyse market trends',
            prompt: 'Provide comprehensive analysis of current market trends and their impact on our strategic positioning. Include competitive landscape assessment and emerging opportunities.',
            color: 'text-green-400 hover:text-green-300',
            category: 'Market Intelligence'
          },
          {
            id: 'opportunity-scoring',
            icon: Target,
            text: 'Score new opportunities',
            prompt: 'Evaluate and score the identified market opportunities based on potential impact, feasibility, and resource requirements. Include ROI projections and implementation timelines.',
            color: 'text-blue-400 hover:text-blue-300',
            category: 'Opportunity Assessment'
          },
          {
            id: 'competitive-analysis',
            icon: AlertTriangle,
            text: 'Competitive threat analysis',
            prompt: 'Analyse competitive threats and recommend strategic responses to maintain market position. Focus on pricing pressure and innovation gaps.',
            color: 'text-red-400 hover:text-red-300',
            category: 'Competitive Intelligence'
          }
        ];

      case '/engineering-dashboard':
        return [
          {
            id: 'ai-efficiency-analysis',
            icon: Brain,
            text: 'Analyse AI efficiency gains',
            prompt: 'Review the current AI-powered efficiency metrics (82% tasks AI-aided, 85hrs saved) and suggest strategies to further optimize AI integration across the development lifecycle.',
            color: 'text-purple-400 hover:text-purple-300',
            category: 'AI Integration'
          },
          {
            id: 'dora-optimization',
            icon: Code,
            text: 'Optimize DORA metrics',
            prompt: 'Analyse current DORA performance (22 deploys/week, 0.5 day lead time) and recommend specific improvements to achieve and maintain elite performer status.',
            color: 'text-green-400 hover:text-green-300',
            category: 'DevOps Excellence'
          },
          {
            id: 'developer-experience',
            icon: Users,
            text: 'Improve developer experience',
            prompt: 'Based on developer satisfaction scores (8.3/10) and focus time metrics (79%), identify opportunities to further enhance developer productivity and innovation.',
            color: 'text-blue-400 hover:text-blue-300',
            category: 'Developer Productivity'
          }
        ];
      
      case '/integrations':
        return [
          {
            id: 'integration-recommendations',
            icon: LinkIcon,
            text: 'Recommend integrations',
            prompt: 'Based on our current tools and workflow, suggest the most valuable integrations we should set up first and explain the benefits of each.',
            color: 'text-indigo-400 hover:text-indigo-300',
            category: 'Integration Planning'
          },
          {
            id: 'data-flow-optimization',
            icon: Activity,
            text: 'Optimize data flows',
            prompt: 'Analyze our current integrations and suggest how to optimize the data flows between systems to reduce manual work and improve data consistency.',
            color: 'text-green-400 hover:text-green-300',
            category: 'Data Management'
          },
          {
            id: 'integration-security',
            icon: Shield,
            text: 'Review integration security',
            prompt: 'Provide best practices for securing our third-party integrations and API connections to minimize security risks while maintaining functionality.',
            color: 'text-red-400 hover:text-red-300',
            category: 'Security'
          }
        ];
      
      case '/customer-dashboard':
        return [
          {
            id: 'customer-segmentation',
            icon: Users,
            text: 'Analyze customer segments',
            prompt: 'Based on the current customer data, identify the most valuable customer segments and recommend targeted strategies to increase retention and lifetime value for each segment.',
            color: 'text-blue-400 hover:text-blue-300',
            category: 'Customer Analysis'
          },
          {
            id: 'feedback-insights',
            icon: MessageCircle,
            text: 'Extract feedback insights',
            prompt: 'Analyze the customer feedback data and sentiment trends to identify the top 3 areas for product improvement that would have the highest impact on customer satisfaction.',
            color: 'text-purple-400 hover:text-purple-300',
            category: 'Voice of Customer'
          },
          {
            id: 'acquisition-optimization',
            icon: UserPlus,
            text: 'Optimize acquisition channels',
            prompt: 'Review the current customer acquisition costs across channels and recommend how to reallocate the marketing budget to maximize ROI and customer quality.',
            color: 'text-green-400 hover:text-green-300',
            category: 'Acquisition Strategy'
          }
        ];
      
      default:
        return [
          {
            id: 'general-analysis',
            icon: BarChart3,
            text: 'Analyse current performance',
            prompt: 'Provide a comprehensive analysis of current performance across all strategic areas including KPIs, experiments, and market position.',
            color: 'text-blue-400 hover:text-blue-300',
            category: 'General Analysis'
          },
          {
            id: 'generate-insights',
            icon: Lightbulb,
            text: 'Generate strategic insights',
            prompt: 'Based on all available data, generate key strategic insights and recommendations for the next quarter.',
            color: 'text-yellow-400 hover:text-yellow-300',
            category: 'Strategic Insights'
          },
          {
            id: 'summarise-signals',
            icon: TrendingUp,
            text: 'Summarise all signals',
            prompt: 'Provide a comprehensive summary of all current market signals and their implications for our business strategy.',
            color: 'text-green-400 hover:text-green-300',
            category: 'Signal Analysis'
          }
        ];
    }
  };

  const handlePromptClick = (prompt: any) => {
    setSelectedPrompt(prompt.prompt);
    setIsChatOpen(true);
  };

  const handleRoleItemClick = (path: string) => {
    console.log('🔄 Navigating to role-specific dashboard:', path);
    
    // Close dropdown and sidebar immediately
    setIsRoleDropdownOpen(false);
    setIsSidebarOpen(false);
    
    // Navigate to the path
    navigate(path);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const dynamicPrompts = getDynamicPrompts();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isRoleDropdownOpen) {
        const target = event.target as Element;
        const dropdown = document.getElementById('role-dropdown');
        const button = document.getElementById('role-dropdown-button');
        
        if (dropdown && button && !dropdown.contains(target) && !button.contains(target)) {
          setIsRoleDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isRoleDropdownOpen]);

  // Get current dashboard name for context display
  const getCurrentDashboardName = () => {
    const currentPath = location.pathname;
    
    // Check role-specific dashboards first
    const roleItem = roleSpecificItems.find(item => item.path === currentPath);
    if (roleItem) {
      return `🎯 ${roleItem.name}`;
    }
    
    // Check core navigation items
    const navItem = navigationItems.find(item => item.path === currentPath);
    if (navItem) {
      switch (currentPath) {
        case '/': return '📊 Strategic Dashboard';
        case '/experiments': return '🧪 Experiment Hub';
        case '/backlog': return '📋 Prioritised Backlog';
        case '/market-insights': return '📈 Market Insights';
        case '/integrations': return '🔌 Integrations';
        default: return navItem.name;
      }
    }
    
    // Fallback for other paths
    return '🏠 Dashboard';
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-40 w-64 bg-gray-800 transform transition-transform duration-300 ease-in-out ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between h-16 bg-gray-900 px-4">
            <div className="flex items-center">
              <h1 className="text-xl lg:text-2xl font-bold text-indigo-400">ADAPT</h1>
              <span className="ml-2 text-xs text-gray-400 hidden sm:inline">Adaptive Framework</span>
            </div>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colours"
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Core Navigation */}
          <nav className="flex-1 mt-8 px-4">
            <ul className="space-y-2">
              {navigationItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center px-3 lg:px-4 py-3 rounded-lg transition-colours duration-200 ${
                        isActive
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`
                    }
                    onClick={() => setIsSidebarOpen(false)}
                  >
                    <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    <span className="text-sm lg:text-base">{item.name}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Enhanced AI Co-Pilot Section */}
          <div className="p-3 lg:p-4 border-t border-gray-700">
            <div className="flex items-center justify-between mb-3 lg:mb-4">
              <div className="flex items-center space-x-2">
                <Brain className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-300">AI Co-Pilot</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0"></div>
                <span className="text-xs text-gray-400">Ready</span>
              </div>
            </div>
            
            {/* Context Indicator */}
            <div className="mb-3 p-2 bg-gray-700 rounded-lg">
              <div className="text-xs text-gray-400 mb-1">Current Context:</div>
              <div className="text-xs font-medium text-indigo-300 break-words">
                {getCurrentDashboardName()}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-2">
              <div className="text-xs text-gray-400 mb-2 flex items-center space-x-1">
                <Sparkles className="w-3 h-3 flex-shrink-0" />
                <span>Quick Actions:</span>
              </div>
              {dynamicPrompts.map((prompt) => (
                <button
                  key={prompt.id}
                  onClick={() => handlePromptClick(prompt)}
                  className={`w-full text-left p-2 lg:p-3 rounded-lg bg-gray-700 hover:bg-gray-600 transition-all duration-200 group border border-gray-600 hover:border-gray-500 ${prompt.color}`}
                >
                  <div className="flex items-start space-x-2">
                    <prompt.icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-300 group-hover:text-white truncate font-medium">
                        {prompt.text}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {prompt.category}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* AI Status Footer */}
            <div className="mt-4 pt-3 border-t border-gray-600">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Model: GPT-4 Turbo</span>
                <span>Role-Aware</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:ml-64 min-h-screen">
        {/* Header with Role Dropdown */}
        <header className="bg-gray-800 border-b border-gray-700 px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colours"
            >
              <Menu size={20} />
            </button>

            {/* Bolt.new Badge */}
            <div className="flex items-center ml-auto lg:ml-0 mr-4">
              <a 
                href="https://bolt.new" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/white_circle_360x360.png" 
                  alt="Built with Bolt.new" 
                  className="w-10 h-10 rounded-full"
                />
              </a>
            </div>

            {/* Role Dropdown */}
            <div className="relative">
              <button
                id="role-dropdown-button"
                onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                className="flex items-center space-x-3 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colours"
              >
                <div className={`p-2 rounded-lg bg-gray-600 ${getRoleColor(userProfile?.role_id || 'viewer')}`}>
                  {getRoleIcon(userProfile?.role_id || 'viewer')}
                </div>
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-medium text-white">{userProfile?.full_name || 'User'}</div>
                  <div className="text-xs text-gray-400">{userProfile?.role_name || 'Viewer'}</div>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isRoleDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isRoleDropdownOpen && (
                <div 
                  id="role-dropdown"
                  className="absolute right-0 mt-2 w-80 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50"
                >
                  {/* User Info */}
                  <div className="p-4 border-b border-gray-700">
                    <div className="flex items-center space-x-3">
                      <div className={`p-3 rounded-lg bg-gray-700 ${getRoleColor(userProfile?.role_id || 'viewer')}`}>
                        {getRoleIcon(userProfile?.role_id || 'viewer')}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">{userProfile?.full_name || 'User'}</div>
                        <div className="text-xs text-gray-400">{userProfile?.role_name || 'Viewer'}</div>
                        <div className="text-xs text-gray-500">{userProfile?.department || 'No Department'} • {userProfile?.role_level && userProfile.role_level >= 3 ? 'Executive Level' : 'Team Member'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Role-Specific Dashboards */}
                  {accessibleRoleItems.length > 0 && (
                    <div className="p-2">
                      <div className="text-xs text-gray-400 px-2 py-1 mb-2">Role-Specific Dashboards</div>
                      {accessibleRoleItems.map((item) => (
                        <button
                          key={item.path}
                          onClick={() => handleRoleItemClick(item.path)}
                          className={`w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-700 rounded-lg transition-colours ${
                            location.pathname === item.path ? 'bg-indigo-600 text-white' : 'text-gray-300'
                          }`}
                        >
                          <item.icon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{item.name}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Account Section */}
                  <div className="p-2 border-t border-gray-700">
                    <div className="text-xs text-gray-400 px-2 py-1 mb-2">Account</div>
                    <button
                      onClick={() => {
                        setIsRoleDropdownOpen(false);
                        navigate('/account');
                      }}
                      className={`w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-700 rounded-lg transition-colours ${
                        location.pathname === '/account' ? 'bg-indigo-600 text-white' : 'text-gray-300'
                      }`}
                    >
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">Profile Settings</span>
                    </button>
                    <button
                      onClick={() => {
                        setIsRoleDropdownOpen(false);
                        navigate('/pricing');
                      }}
                      className={`w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-700 rounded-lg transition-colours ${
                        location.pathname === '/pricing' ? 'bg-indigo-600 text-white' : 'text-gray-300'
                      }`}
                    >
                      <CreditCard className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">Subscription</span>
                    </button>
                  </div>

                  {/* Admin Section */}
                  <div className="p-2 border-t border-gray-700">
                    <div className="text-xs text-gray-400 px-2 py-1 mb-2">Administration</div>
                    <button
                      onClick={() => handleRoleItemClick('/user-management')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-700 rounded-lg transition-colours ${
                        location.pathname === '/user-management' ? 'bg-indigo-600 text-white' : 'text-gray-300'
                      }`}
                    >
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">User Management</span>
                    </button>
                    <button
                      onClick={() => handleRoleItemClick('/role-permissions')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-700 rounded-lg transition-colours ${
                        location.pathname === '/role-permissions' ? 'bg-indigo-600 text-white' : 'text-gray-300'
                      }`}
                    >
                      <Shield className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">Role Permissions</span>
                    </button>
                    <button
                      onClick={() => handleRoleItemClick('/system-settings')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-700 rounded-lg transition-colours ${
                        location.pathname === '/system-settings' ? 'bg-indigo-600 text-white' : 'text-gray-300'
                      }`}
                    >
                      <Settings className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">System Settings</span>
                    </button>
                  </div>

                  {/* Sign Out */}
                  <div className="p-2 border-t border-gray-700">
                    <button 
                      onClick={handleSignOut}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-700 rounded-lg transition-colours text-red-400"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="text-sm">Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="p-4 lg:p-8">
          {children}
          
          {/* Only show subscription banner on the strategic dashboard */}
          {location.pathname === '/' && <SubscriptionBanner />}
          
          {/* Footer with subscription link */}
          <div className="mt-8 pt-4 border-t border-gray-700 text-center">
            <div className="text-xs text-gray-500 flex flex-wrap justify-center gap-2">
              <Link to="/pricing" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                View subscription plans
              </Link>
              <span className="mx-2">•</span>
              <Link to="/terms" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                Terms & Conditions
              </Link>
              <span className="mx-2">•</span>
              <span>© 2025 ADAPT Framework</span>
            </div>
          </div>
        </main>
      </div>

      {/* Enhanced AI Chat Toggle Button */}
      <button
        onClick={() => setIsChatOpen(!isChatOpen)}
        className={`fixed bottom-6 right-6 w-14 h-14 lg:w-16 lg:h-16 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-full shadow-xl transition-all duration-300 flex items-center justify-center z-50 hover:scale-110 ${
          isChatOpen ? 'rotate-180' : ''
        }`}
      >
        <Bot size={24} className="lg:w-7 lg:h-7" />
        {!isChatOpen && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-pulse"></div>
        )}
      </button>

      {/* AI Chat Panel */}
      <AIChat 
        isOpen={isChatOpen} 
        onClose={() => {
          setIsChatOpen(false);
          setSelectedPrompt('');
        }}
        initialPrompt={selectedPrompt}
      />

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default Layout;